import React, { useState } from 'react';
import InboxView from './InboxView';

/**
 * 24/7 Auto-Apply tab content. Server-side queue powered by Greenhouse,
 * Lever, Workable, and Ashby public APIs.
 *
 * Renders:
 *   - Section header
 *   - Refresh + pause buttons
 *   - UsageStrip (plan badge + daily/hourly/discoveries counters)
 *   - Mode / threshold / status filter controls
 *   - Skeleton loader while fetching
 *   - Empty state (varies by status filter + plan)
 *   - Job table (when jobs are loaded)
 *
 * Pure presentation. Parent owns all queue state + handlers.
 */
export default function QueueTab({
  // Data
  usage,
  resetCountdown,
  queueJobs,
  queueCounts,
  queueLoading,
  queueRefreshing,

  // Settings
  queueMode,
  setQueueMode,
  queueThreshold,
  setQueueThreshold,
  queueStatusFilter,
  setQueueStatusFilter,
  queueSettingsSavedAt,

  // Handlers
  refreshQueue,
  onDrainNow,
  queueDraining,
  togglePause,
  saveQueueSettings,
  decideJob,

  // Navigation (for empty-state CTAs)
  navigate,
  setActiveTab,

  // Nylas (Gmail/Outlook integration for OTP auto-resolution + inbox)
  nylas,
  onConnectNylas,
  onDisconnectNylas,
  apiBaseUrl,
}) {
  const discoveryAtCap = usage?.discoveries && !usage.discoveries.allowed;
  // Sub-tab inside Auto Apply: 'jobs' (the queue) | 'inbox' (Nylas messages).
  // Mirrors AIApply's layout where Jobs and Inbox are equal-weight tabs.
  const [subTab, setSubTab] = useState('jobs');

  return (
    <div key="tab-queue" className="ajp-tab-pane">

      {/* Section header removed — page already has the QuickApply / Auto Apply
          pill switcher at the top, which doubles as the section identifier. */}

      {/* ─── Gmail / Inbox connection (Nylas) — connected state is now
            inlined into the Jobs/Inbox tab row below. The CTA card stays
            here for the unconnected state since it's a real call-to-action. */}
      {nylas?.configured && !nylas.connected && (
        <div style={{
          marginTop: 8, marginBottom: 16, padding: 16, borderRadius: 12,
          border: '1px solid rgba(124,58,237,0.35)',
          background: 'rgba(40,20,60,0.4)',
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
        }}>
          <div style={{ fontSize: 26 }}>📬</div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontWeight: 700, color: '#fff', marginBottom: 2 }}>
              Connect your inbox for hands-free apply
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.4 }}>
              Solves email-verification challenges (Discord, Reddit, etc.) automatically. Replies from recruiters land in Terview.
            </div>
          </div>
          {nylas.loading ? (
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>Loading…</span>
          ) : (
            <button
              onClick={onConnectNylas}
              style={{
                padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                background: '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer',
              }}>
              Connect Gmail / Outlook
            </button>
          )}
        </div>
      )}

      {/* ─── Jobs / Inbox sub-tabs (AIApply-style) ─── */}
      {nylas?.connected && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 4,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          marginTop: 16, marginBottom: 0,
        }}>
          {[
            { key: 'jobs',  label: 'Jobs',  icon: '📥' },
            { key: 'inbox', label: 'Inbox', icon: '📬' },
          ].map(({ key, label, icon }) => {
            const active = subTab === key;
            return (
              <button
                key={key}
                onClick={() => setSubTab(key)}
                style={{
                  padding: '10px 18px',
                  background: 'transparent',
                  color: active ? '#fff' : 'rgba(255,255,255,0.55)',
                  border: 'none',
                  borderBottom: active ? '2px solid #7c3aed' : '2px solid transparent',
                  fontWeight: active ? 700 : 500,
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'color 0.15s, border-color 0.15s',
                }}>
                <span style={{ marginRight: 6 }}>{icon}</span>{label}
              </button>
            );
          })}
          {/* Connection pill, right-aligned in the same row as the tabs. */}
          <div style={{
            marginLeft: 'auto', marginBottom: 6,
            padding: '6px 12px', borderRadius: 999,
            border: '1px solid rgba(74,222,128,0.25)',
            background: 'rgba(20,40,30,0.35)',
            display: 'inline-flex', alignItems: 'center', gap: 10,
            fontSize: 12,
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#4ade80', boxShadow: '0 0 6px rgba(74,222,128,0.8)',
            }} />
            <span style={{ color: 'rgba(255,255,255,0.85)' }}>
              <span style={{ color: 'rgba(255,255,255,0.55)' }}>Inbox:</span>{' '}
              <span style={{ fontWeight: 600 }}>{nylas.email}</span>
            </span>
            {!nylas.loading && (
              <button
                onClick={onDisconnectNylas}
                style={{
                  padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  background: 'transparent', color: 'rgba(250,204,21,0.8)',
                  border: '1px solid rgba(250,204,21,0.3)', cursor: 'pointer',
                }}>
                Disconnect
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── INBOX SUB-TAB ───
          Stays mounted while connected so toggling between Jobs / Inbox
          doesn't re-fetch the message list every time the user switches
          sub-tabs. Visibility is driven by the `visible` prop (display:
          none when off-screen); the message list + selected-email body
          stay in component state and switching back is instant. */}
      {nylas?.connected && (
        <InboxView apiBaseUrl={apiBaseUrl} isActive={subTab === 'inbox'} />
      )}

      {/* ─── JOBS SUB-TAB (the queue) ───
          Two-column layout mirroring QuickApply: LEFT holds the status
          cards + filter chips + mode/threshold (the control surface).
          RIGHT holds just the jobs table, which scrolls internally. */}
      {(!nylas?.connected || subTab === 'jobs') && (
      <div className="ajp-queue-two-col" style={{ marginTop: 16 }}>
        <div className="ajp-queue-left">

        {/* ─── Status row: 3 cards stacked vertically in left column ─── */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14,
        }}>
          {/* Card 1: Status + Pause inline */}
          <div style={{
            padding: 12, borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(20,10,40,0.55)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.55)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Status
              </div>
              {usage && (
                <button onClick={togglePause}
                  style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: 'transparent',
                    color: usage.queuePaused ? '#facc15' : 'rgba(255,255,255,0.6)',
                    border: `1px solid ${usage.queuePaused ? '#facc15' : 'rgba(255,255,255,0.15)'}`,
                    cursor: 'pointer',
                  }}>
                  {usage.queuePaused ? '▶ Resume' : '⏸ Pause'}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: usage?.queuePaused ? '#facc15' : '#4ade80',
                boxShadow: usage?.queuePaused ? 'none' : '0 0 0 4px rgba(74,222,128,0.18)',
              }} />
              <span style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>
                {usage?.queuePaused ? 'Paused' : 'Applying Now'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
              We send applications in the background.
            </div>
          </div>

          {/* Card 2: Applications submitted (today) */}
          <div style={{
            padding: 12, borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(20,10,40,0.55)',
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.55)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Applications Today
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#fff', marginTop: 8, lineHeight: 1 }}>
              {usage?.applies24_7?.used ?? 0}
              <span style={{ fontSize: 16, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginLeft: 4 }}>
                / {usage?.applies24_7?.limit ?? 0}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>
              {usage?.plan ? `${usage.plan.toUpperCase()} plan` : ''} · resets {resetCountdown ? `in ${Math.floor(resetCountdown/3600)}h ${Math.floor((resetCountdown%3600)/60)}m` : 'at 00:00 UTC'}
            </div>
          </div>

          {/* Card 3: Discoveries (matches found today) */}
          <div style={{
            padding: 12, borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(20,10,40,0.55)',
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.55)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Job Matches Found
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#fff', marginTop: 8, lineHeight: 1 }}>
              {usage?.discoveries?.used ?? 0}
              <span style={{ fontSize: 16, fontWeight: 600, color: 'rgba(255,255,255,0.45)', marginLeft: 4 }}>
                / {usage?.discoveries?.limit ?? 0}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>
              Across Greenhouse · Lever · Workable · Ashby
            </div>
          </div>
        </div>

        {/* ─── Mode + threshold (collapsed into a single row) ─── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap',
          padding: '10px 14px', borderRadius: 10,
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
          fontSize: 13,
        }}>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Mode</span>
          <select value={queueMode}
            onChange={e => { setQueueMode(e.target.value); saveQueueSettings(e.target.value, queueThreshold); }}
            style={{ padding: '5px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)' }}>
            <option value="review">Review every job</option>
            <option value="hybrid">Hybrid (≥ threshold)</option>
            <option value="auto">Auto-apply</option>
          </select>

          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginLeft: 8 }}>Threshold</span>
          <input type="number" min="0" max="100" value={queueThreshold}
            onChange={e => setQueueThreshold(parseInt(e.target.value, 10) || 0)}
            onBlur={() => saveQueueSettings(queueMode, queueThreshold)}
            style={{ width: 60, padding: '5px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.12)' }} />

          {queueSettingsSavedAt && (
            <span style={{ marginLeft: 'auto', color: '#4ade80', fontSize: 12, fontWeight: 600 }}>
              ✓ Saved
            </span>
          )}
        </div>

        </div>{/* /ajp-queue-left */}

        <div className="ajp-queue-right">

        {/* ─── Filter chips + refresh / drain actions (above the jobs table) ─── */}
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
          flexShrink: 0,
        }}>
          {[
            { key: 'waiting',  label: 'Waiting',   countKey: 'waiting' },
            { key: 'approved', label: 'In flight', countKey: 'approved' },
            { key: 'applied',  label: 'Applied',   countKey: 'applied' },
            { key: 'failed',   label: 'Failed',    countKey: 'failed' },
            { key: 'rejected', label: 'Rejected',  countKey: 'rejected' },
            { key: '',         label: 'All',       countKey: 'total' },
          ].map(({ key, label, countKey }) => {
            const active = queueStatusFilter === key;
            const count = queueCounts?.[countKey] ?? 0;
            return (
              <button
                key={key || 'all'}
                onClick={() => setQueueStatusFilter(key)}
                style={{
                  padding: '7px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                  background: active ? 'rgba(124,58,237,0.18)' : 'transparent',
                  color: active ? '#c4a3ff' : 'rgba(255,255,255,0.65)',
                  border: `1px solid ${active ? 'rgba(124,58,237,0.5)' : 'rgba(255,255,255,0.1)'}`,
                  cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
                }}>
                {label}
                <span style={{
                  marginLeft: 6, padding: '1px 7px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                  background: active ? 'rgba(124,58,237,0.35)' : 'rgba(255,255,255,0.08)',
                  color: active ? '#fff' : 'rgba(255,255,255,0.7)',
                }}>
                  {count}
                </span>
              </button>
            );
          })}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={refreshQueue}
              disabled={queueRefreshing || discoveryAtCap}
              title={discoveryAtCap ? `Daily discovery cap reached (${usage.discoveries.used}/${usage.discoveries.limit}).` : ''}
              style={{
                padding: '8px 16px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                background: queueRefreshing ? '#555' : discoveryAtCap ? 'rgba(255,255,255,0.05)' : '#7c3aed',
                color: discoveryAtCap ? 'rgba(255,255,255,0.4)' : '#fff', border: 'none',
                cursor: queueRefreshing ? 'wait' : discoveryAtCap ? 'not-allowed' : 'pointer',
              }}>
              {queueRefreshing ? '🔄 Discovering…' : discoveryAtCap ? '🛑 Daily cap reached' : '🔄 Refresh'}
            </button>
            {/* Manual drain — fires the worker immediately for users who
                bulk-edited the queue and don't want to wait for the next
                hourly cron. Disabled when nothing is approved. */}
            {(queueCounts?.approved ?? 0) > 0 && (
              <button
                onClick={onDrainNow}
                disabled={queueDraining}
                title={`Apply ${queueCounts.approved} approved job(s) now`}
                style={{
                  padding: '8px 16px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                  background: queueDraining ? '#555' : 'transparent',
                  color: queueDraining ? '#aaa' : '#a78bfa',
                  border: '1px solid rgba(167,139,250,0.5)',
                  cursor: queueDraining ? 'wait' : 'pointer',
                }}>
                {queueDraining ? '⏳ Draining…' : `▶ Drain ${queueCounts.approved}`}
              </button>
            )}
          </div>
        </div>

        {/* Skeleton loader during fetch */}
        {queueLoading && (
          <div className="ajp-skel-table">
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="ajp-skel-row">
                <div className="ajp-skeleton ajp-skel-cell tall" />
                <div className="ajp-skeleton ajp-skel-cell" />
                <div className="ajp-skeleton ajp-skel-cell" />
                <div className="ajp-skeleton ajp-skel-cell short" />
                <div className="ajp-skeleton ajp-skel-cell short" />
                <div className="ajp-skeleton ajp-skel-cell short" />
                <div className="ajp-skeleton ajp-skel-cell short" />
              </div>
            ))}
          </div>
        )}

        {/* Free-plan empty state — different copy than the per-status states */}
        {!queueLoading && usage?.plan === 'free' && queueJobs.length === 0 && (
          <div className="ajp-empty">
            <div className="ajp-empty-icon">🔒</div>
            <div className="ajp-empty-title">24/7 Auto-Apply isn't on your plan.</div>
            <div className="ajp-empty-sub">
              You're on the <strong>Free</strong> plan. Live Auto-Apply (LinkedIn / Dice / etc.)
              works fully — but background applies via Greenhouse · Lever · Workable · Ashby
              need a paid plan. <strong>Lite ($29.99/mo)</strong> unlocks 5 per day.
            </div>
            <div className="ajp-empty-actions">
              <button className="ajp-empty-btn" onClick={() => window.dispatchEvent(new CustomEvent('openPricingModal'))}>
                ✨ See plans
              </button>
              <button className="ajp-empty-btn secondary" onClick={() => setActiveTab('live')}>
                Use Live Auto-Apply instead
              </button>
            </div>
          </div>
        )}

        {/* Per-status empty states — only shown to paid users */}
        {!queueLoading && usage?.plan !== 'free' && queueJobs.length === 0 && (() => {
          const filter = queueStatusFilter || 'all';
          const config = {
            waiting:  {
              icon: '🔍',
              title: 'No jobs to review yet.',
              sub: 'Click Refresh Queue to discover matches across Greenhouse, Lever, Workable, and Ashby.',
              primary: { label: '🔄 Refresh Queue', onClick: refreshQueue, disabled: queueRefreshing },
              secondary: null,
            },
            approved: {
              icon: '⏳',
              title: 'No jobs in flight.',
              sub: 'When you approve a waiting job, it lands here while we apply on your behalf.',
              primary: { label: '← Back to waiting', onClick: () => setQueueStatusFilter('waiting') },
            },
            applied:  {
              icon: '🎯',
              title: 'No applications submitted yet.',
              sub: 'Approve a waiting job to start applying. Successful submissions appear here.',
              primary: { label: '← Back to waiting', onClick: () => setQueueStatusFilter('waiting') },
            },
            failed:   {
              icon: '✨',
              title: 'No failures — clean run.',
              sub: 'Failed applications would show up here with a reason. Nothing to see for now.',
              primary: { label: '← Back to waiting', onClick: () => setQueueStatusFilter('waiting') },
            },
            rejected: {
              icon: '🗑️',
              title: 'Nothing rejected.',
              sub: 'Jobs you reject from the waiting list will end up here for your records.',
              primary: { label: '← Back to waiting', onClick: () => setQueueStatusFilter('waiting') },
            },
            all: {
              icon: '📥',
              title: 'Your queue is empty.',
              sub: 'Click Refresh Queue to start. Your discovered, approved, applied, and rejected jobs will all show up here.',
              primary: { label: '🔄 Refresh Queue', onClick: refreshQueue, disabled: queueRefreshing },
              secondary: { label: 'Edit profile', onClick: () => navigate('/profile') },
            },
          };
          const c = config[filter] || config.waiting;
          return (
            <div className="ajp-empty">
              <div className="ajp-empty-icon">{c.icon}</div>
              <div className="ajp-empty-title">{c.title}</div>
              <div className="ajp-empty-sub">{c.sub}</div>
              <div className="ajp-empty-actions">
                {c.primary && (
                  <button className="ajp-empty-btn" onClick={c.primary.onClick} disabled={c.primary.disabled}>
                    {c.primary.label}
                  </button>
                )}
                {c.secondary && (
                  <button className="ajp-empty-btn secondary" onClick={c.secondary.onClick}>
                    {c.secondary.label}
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Jobs table — internal vertical scroll, sticky header. */}
        {queueJobs.length > 0 && (
          <div className="ajp-queue-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'rgba(224,163,255,0.6)', borderBottom: '1px solid rgba(224,163,255,0.2)' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Score</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Title</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Company</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Location</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Platform</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {queueJobs.map(j => (
                  <tr key={j.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <td style={{ padding: '10px', textAlign: 'left', fontWeight: 700, color: j.match_score >= 80 ? '#4ade80' : j.match_score >= 60 ? '#facc15' : '#f87171' }}>
                      {j.match_score ?? '—'}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'left' }}>
                      <a href={j.apply_url} target="_blank" rel="noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>{j.title}</a>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'left', color: 'rgba(255,255,255,0.8)' }}>{j.company}</td>
                    <td style={{ padding: '10px', textAlign: 'left', color: 'rgba(255,255,255,0.6)' }}>{j.location || '—'}</td>
                    <td style={{ padding: '10px', textAlign: 'left', color: 'rgba(255,255,255,0.6)' }}>{j.platform}</td>
                    <td style={{ padding: '10px', textAlign: 'left' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: j.status === 'applied' ? 'rgba(74,222,128,0.2)' :
                                    j.status === 'failed'   ? 'rgba(248,113,113,0.2)' :
                                    j.status === 'rejected' ? 'rgba(160,160,160,0.2)' :
                                    j.status === 'approved' ? 'rgba(250,204,21,0.2)' :
                                                              'rgba(124,58,237,0.2)',
                        color:      j.status === 'applied' ? '#4ade80' :
                                    j.status === 'failed'   ? '#f87171' :
                                    j.status === 'rejected' ? '#a0a0a0' :
                                    j.status === 'approved' ? '#facc15' :
                                                              '#c4a3ff',
                      }}>{j.status}</span>
                      {j.status === 'failed' && j.failure_reason && (
                        <div title={j.failure_reason}
                             style={{ marginTop: 4, fontSize: 11, color: '#f87171', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {j.failure_reason}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'left' }}>
                      {j.status === 'waiting' && (() => {
                        const a = usage?.applies24_7 || { used: 0, limit: 0 };
                        const atCap = a.limit > 0 && a.used >= a.limit;
                        const blockedByPlan = a.limit === 0;
                        const approveTitle = blockedByPlan
                          ? 'Upgrade to Pro or Max to enable 24/7 auto-apply'
                          : atCap
                            ? 'Daily cap reached — will be queued for tomorrow'
                            : 'Approve and start applying';
                        return (
                          <>
                            <button onClick={() => decideJob(j.id, 'approve', j)}
                              disabled={blockedByPlan}
                              title={approveTitle}
                              style={{
                                marginRight: 6, padding: '4px 10px', borderRadius: 6,
                                fontSize: 12, fontWeight: 600,
                                background: blockedByPlan ? '#444' : atCap ? '#a3e635aa' : '#4ade80',
                                color: '#0a0a0a',
                                border: 'none', cursor: blockedByPlan ? 'not-allowed' : 'pointer',
                                opacity: blockedByPlan ? 0.5 : 1,
                              }}>
                              {blockedByPlan ? '🔒 Approve' : atCap ? 'Approve →tomorrow' : 'Approve'}
                            </button>
                            <button onClick={() => decideJob(j.id, 'reject')}
                              style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: 'transparent', color: '#f87171', border: '1px solid #f87171', cursor: 'pointer' }}>
                              Reject
                            </button>
                          </>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>{/* /ajp-queue-right */}
      </div>
      )}
    </div>
  );
}